'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import {
  getConsent,
  hasDecided,
  onConsentChange,
  revokeConsent,
  setConsent,
  type ConsentState,
} from '@/lib/analytics/consent';

/**
 * Self-service consent editor.
 *
 * Use cases:
 * - Admin → Settings → Datenschutz (for logged-in users)
 * - Public footer link "Cookie-Einstellungen" (re-opens the choice)
 *
 * Re-rendering the cookie banner without consent state isn't trivial in Next.js
 * App Router, so we render inline toggles + a "reset to undecided" button.
 */
export function ConsentSettings() {
  const t = useTranslations('components.cookieBanner');
  const [state, setState] = useState<ConsentState | null>(null);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    const sync = (next: ConsentState | null) => {
      setState(next);
      setAnalytics(!!next?.analytics);
      setMarketing(!!next?.marketing);
    };
    sync(getConsent());
    return onConsentChange(sync);
  }, []);

  const save = () => {
    const result = setConsent({ analytics, marketing });
    setSavedAt(result.decidedAt);
  };

  const reset = () => {
    revokeConsent();
    setAnalytics(false);
    setMarketing(false);
    setSavedAt(null);
  };

  const hasChoice = hasDecided();

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-gray-900">{t('necessaryTitle')}</h4>
            <p className="text-sm text-gray-600">{t('necessaryDesc')}</p>
          </div>
          <span className="text-sm text-gray-500">{t('alwaysActive')}</span>
        </div>
      </div>

      <label className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
        <div>
          <h4 className="font-medium text-gray-900">{t('analyticsTitle')}</h4>
          <p className="text-sm text-gray-600">{t('analyticsDesc')}</p>
        </div>
        <input
          type="checkbox"
          checked={analytics}
          onChange={(e) => setAnalytics(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600"
        />
      </label>

      <label className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-white p-4 hover:bg-gray-50">
        <div>
          <h4 className="font-medium text-gray-900">{t('marketingTitle')}</h4>
          <p className="text-sm text-gray-600">{t('marketingDesc')}</p>
        </div>
        <input
          type="checkbox"
          checked={marketing}
          onChange={(e) => setMarketing(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-blue-600"
        />
      </label>

      <div className="flex flex-wrap gap-3">
        <Button onClick={save} className="bg-blue-600 text-white hover:bg-blue-700">
          {t('saveSettings')}
        </Button>
        {hasChoice && (
          <Button onClick={reset} variant="outline">
            {t('reset')}
          </Button>
        )}
      </div>

      {state && (
        <p className="text-xs text-gray-500">
          {t('lastDecidedAt', {
            date: new Date(savedAt || state.decidedAt).toLocaleString(),
          })}
        </p>
      )}
    </div>
  );
}
